import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { BrandProvider } from "@/context/BrandContext";

import LandingPage from "@/pages/LandingPage";
import MerchantLandingPage from "@/pages/MerchantLandingPage";
import MembersLandingPage from "@/pages/MembersLandingPage";
import PartnersPage from "@/pages/PartnersPage";
import BecomePartnerPage from "@/pages/BecomePartnerPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminSubmissions from "@/pages/admin/AdminSubmissions";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminUsers from "@/pages/admin/AdminUsers";
import MerchantDashboard from "@/pages/merchant/MerchantDashboard";
import PreviewBanner from "@/components/PreviewBanner";

function AppRoutes() {
  const loc = useLocation();

  // Scroll to top on route change (ignore hash scrolling, browser handles those)
  useEffect(() => {
    if (!loc.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [loc.pathname]);

  if (loc.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <PreviewBanner />
      <Routes>
        <Route path="/" element={<MembersLandingPage />} />
        <Route path="/pro" element={<MerchantLandingPage />} />
        <Route path="/nos-partenaires" element={<MembersLandingPage />} />
        <Route path="/apercu" element={<LandingPage />} />
        <Route path="/partenaires" element={<PartnersPage />} />
        <Route path="/devenir-partenaire" element={<BecomePartnerPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
        <Route path="/partenaire/login" element={<AdminLoginPage merchantMode />} />
        <Route path="/partenaire/mot-de-passe-oublie" element={<ForgotPasswordPage merchantMode />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/partenaire" element={<MerchantDashboard />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="partenaires" element={<AdminPartners />} />
          <Route path="soumissions" element={<AdminSubmissions />} />
          <Route path="parametres" element={<AdminSettings />} />
          <Route path="configuration" element={<AdminConfig />} />
          <Route path="utilisateurs" element={<AdminUsers />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  // Defensive: remove the Emergent preview badge whose inline styles are corrupted
  useEffect(() => {
    const kill = () => {
      const el = document.getElementById("emergent-badge");
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
    kill();
    const id = setInterval(kill, 1000);
    return () => clearInterval(id);
  }, []);

  // DIAGNOSTIC: Find any dark rectangle overlapping the hero h1 and highlight it
  useEffect(() => {
    if (!window.location.search.includes("debug")) return;
    const scan = () => {
      const h1 = document.querySelector('[data-testid="ma-hero-title"]');
      if (!h1) return;
      const hr = h1.getBoundingClientRect();
      document.querySelectorAll("*").forEach((el) => {
        if (el === h1 || h1.contains(el)) return;
        const r = el.getBoundingClientRect();
        const cs = window.getComputedStyle(el);
        if (r.width < 40 || r.height < 40) return;
        const overlap = !(r.right < hr.left || r.left > hr.right || r.bottom < hr.top || r.top > hr.bottom);
        if (!overlap) return;
        const bg = cs.backgroundColor;
        const m = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/);
        if (!m) return;
        const bright = (+m[1] + +m[2] + +m[3]) / 3;
        const alpha = m[4] !== undefined ? +m[4] : 1;
        if (bright < 100 && alpha > 0.3 && cs.opacity !== "0" && cs.visibility !== "hidden" && cs.display !== "none") {
          el.style.outline = "3px solid red";
          el.style.outlineOffset = "2px";
          el.setAttribute("data-debug-culprit", "true");
          const label = document.createElement("div");
          label.textContent = `${el.tagName}.${(el.className || "").toString().slice(0, 30)} bg=${bg}`;
          label.style.cssText = "position:fixed;top:10px;left:10px;background:yellow;color:black;padding:6px;font:12px monospace;z-index:99999;max-width:90vw;word-break:break-word;";
          document.body.appendChild(label);
        }
      });
    };
    setTimeout(scan, 1500);
    setTimeout(scan, 3000);
  }, []);

  return (
    <LangProvider>
      <BrandProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </BrandProvider>
    </LangProvider>
  );
}

export default App;
